import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AuthorBox from "../components/AuthorBox";
import ContactModal from "../components/ContactModal";
import SEOHead from "../components/SEOHead";
import { 
  Users, 
  Target, 
  Award, 
  Lightbulb,
  CheckCircle,
  ArrowRight,
  Star,
  Trophy,
  Globe,
  Settings,
  MapPin,
  Building,
  Briefcase
} from "lucide-react";

const About = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const seoData = {
    title: "Über BROVKO - KI-Transformation Partner seit 2011 | BROVKO",
    description: "BROVKO ist Ihr Partner für nachhaltige KI-Transformation. Seit 2011 begleiten wir Unternehmen auf ihrem Weg zur erfolgreichen AI-Integration.",
    keywords: "BROVKO, KI-Beratung, AI Transformation, Unternehmensberatung, KI Partner",
    canonicalUrl: "/ueber-uns",
    ogTitle: "Über BROVKO - KI-Transformation Partner",
    ogDescription: "Seit 2011 Ihr Partner für nachhaltige KI-Transformation und AI-Integration in Unternehmen."
  };

  // Modal handlers
  const openContactModal = () => {
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  const navigateToServices = () => {
    navigate('/leistungsangebot');
  };

  const values = [
    {
      icon: Lightbulb,
      title: t('about.values.innovation.title'),
      description: t('about.values.innovation.description'),
      color: "bg-blue-500"
    },
    {
      icon: Award,
      title: t('about.values.excellence.title'),
      description: t('about.values.excellence.description'),
      color: "bg-brand"
    },
    {
      icon: Users,
      title: t('about.values.customer.title'), 
      description: t('about.values.customer.description'),
      color: "bg-purple-500"
    },
    {
      icon: CheckCircle,
      title: t('about.values.integrity.title'),
      description: t('about.values.integrity.description'),
      color: "bg-green-500"
    },
    {
      icon: Star,
      title: t('about.values.teamwork.title'),
      description: t('about.values.teamwork.description'),
      color: "bg-orange-500"
    }
  ];

  const locations = [
    {
      icon: Building,
      city: "Hannover",
      country: t('about.locations.germany'),
      role: t('about.locations.hannover.role'),
      description: t('about.locations.hannover.description'),
      color: "bg-blue-500"
    },
    {
      icon: Briefcase,
      city: "Düsseldorf", 
      country: t('about.locations.germany'),
      role: t('about.locations.dusseldorf.role'),
      description: t('about.locations.dusseldorf.description'),
      color: "bg-brand"
    },
    {
      icon: Settings,
      city: "Breslau",
      country: t('about.locations.poland'),
      role: t('about.locations.breslau.role'),
      description: t('about.locations.breslau.description'),
      color: "bg-purple-500"
    },
    {
      icon: Lightbulb,
      city: "Haifa",
      country: t('about.locations.israel'),
      role: t('about.locations.haifa.role'),
      description: t('about.locations.haifa.description'),
      color: "bg-orange-500"
    }
  ];

  return (
    <div className="theme-brand bg-surface text-ink min-h-screen bg-surface-sunken">
      <SEOHead {...seoData} />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-40 pb-10 bg-gradient-to-br from-slate-900 to-teal-900">
        <div className="container mx-auto px-4">
          <div className=" text-white">
            <h1 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] mb-4">
              {t('about.hero.title')}
            </h1>
            <p className="text-base md:text-lg text-ink-muted max-w-4xl mx-auto">
              {t('about.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-[64ch] mb-8">
              <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-3">
                {t('about.team.title')}
              </h2>
              <p className="text-base text-ink-muted leading-relaxed max-w-4xl mx-auto">
                {t('about.team.description')}
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-ink mb-4">
                  {t('about.services.title')}
                </h3>
                <p className="text-ink-muted text-sm leading-relaxed mb-4">
                  {t('about.services.description')}
                </p>
                <Button 
                  className="bg-brand hover:bg-brand-600 text-ink-onBrand text-sm"
                  onClick={navigateToServices}
                >
                  {t('about.services.button')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
              <div className="bg-surface p-6 rounded-lg shadow-lg">
                <div className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-brand-400 mb-1">20+</div>
                <div className="text-lg font-semibold text-ink mb-1">
                  {t('about.team.specialists')}
                </div>
                <div className="text-ink-muted text-sm">
                  {t('about.team.expertise')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 md:py-28 bg-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-5">
                  <div className="bg-brand/15 p-3 rounded-full w-fit mb-4">
                    <Target className="h-6 w-6 text-brand-400" />
                  </div>
                  <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-ink mb-3">
                    {t('about.mission.title')}
                  </h3>
                  <p className="text-ink-muted text-sm leading-relaxed">
                    {t('about.mission.description')}
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-5">
                  <div className="bg-blue-500/15 p-3 rounded-full w-fit mb-4">
                    <Star className="h-6 w-6 text-blue-400" />
                  </div>
                  <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-ink mb-3">
                    {t('about.vision.title')}
                  </h3>
                  <p className="text-ink-muted text-sm leading-relaxed">
                    {t('about.vision.description')}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-8">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-3">
              {t('about.values.title')}
            </h2>
            <p className="text-base text-ink-muted max-w-[62ch]">
              {t('about.values.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 max-w-7xl mx-auto">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="group border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="p-4 text-center">
                    <div className={`p-3 rounded-full ${value.color} mx-auto mb-3 w-fit`}>
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-base font-bold text-ink mb-2">
                      {value.title}
                    </h3>
                    <p className="text-xs text-ink-muted leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Company History */}
      <section className="py-20 md:py-28 bg-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-6">
              {t('about.history.title')}
            </h2>
            <div className="bg-brand/10 p-6 rounded-lg mb-6">
              <div className="text-4xl font-bold text-brand-400 mb-2">2011</div>
              <div className="text-lg font-semibold text-ink mb-1">
                {t('about.history.founding')}
              </div>
            </div>
            <p className="text-base text-ink-muted leading-relaxed">
              {t('about.history.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Resources/Locations */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-8">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-3">
              {t('about.resources.title')}
            </h2>
            <p className="text-base text-ink-muted max-w-[62ch]">
              {t('about.resources.subtitle')}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-4 max-w-7xl mx-auto">
            {locations.map((location, index) => {
              const Icon = location.icon;
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-5 text-center">
                    <div className={`p-3 rounded-full ${location.color} mx-auto mb-4 w-fit`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex items-center justify-center gap-1.5 mb-1">
                      <MapPin className="h-3 w-3 text-ink-faint" />
                      <span className="text-base font-semibold text-ink">
                        {location.city}, {location.country}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-brand-400 mb-2">
                      {location.role}
                    </h3>
                    <p className="text-ink-muted text-sm leading-relaxed">
                      {location.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-gradient-to-r from-slate-900 to-teal-900">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-3">
            {t('about.cta.title')}
          </h2>
          <p className="text-base text-ink-muted max-w-[62ch] mb-6">
            {t('about.cta.subtitle')}
          </p>
          <Button 
            size="lg" 
            className="bg-brand hover:bg-brand-600 text-ink-onBrand px-6 py-3 text-sm font-semibold"
            onClick={openContactModal}
          >
            {t('about.cta.button')}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <div className="container mx-auto px-4">
        <AuthorBox />
      </div>

      <Footer />

      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
        pageSource="Über uns"
        buttonContext="CTA Section"
        buttonText="Gespräch vereinbaren"
      />
    </div>
  );
};

export default About;